import { useState, useEffect, useRef, useCallback } from "react";

//  Work Offline Hook 
export function useWorkOffline(addToast) {
  const [isOffline, setIsOffline] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [queue, setQueue] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sdr_offline_queue") || "[]"); }
    catch { return []; }
  });
  const [browserOnline, setBrowserOnline] = useState(navigator.onLine);
  const syncRef = useRef(false);

  // Detect real browser connectivity
  useEffect(() => {
    const onOnline  = () => { setBrowserOnline(true);  };
    const onOffline = () => { setBrowserOnline(false); };
    window.addEventListener("online",  onOnline);
    window.addEventListener("offline", onOffline);
    return () => { window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); };
  }, []);

  // Persist queue to localStorage
  useEffect(() => {
    localStorage.setItem("sdr_offline_queue", JSON.stringify(queue));
  }, [queue]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (browserOnline && !isOffline && queue.length > 0 && !syncRef.current) {
      syncQueue();
    }
  }, [browserOnline, isOffline]);

  const enqueue = useCallback((action) => {
    const item = { id: Date.now(), ...action, status: "pending", queuedAt: new Date().toISOString() };
    setQueue(p => [...p, item]);
    addToast(`Queued: ${action.label}  will sync when online`, "info");
    return item.id;
  }, [addToast]);

  const syncQueue = useCallback(async () => {
    if (syncRef.current || queue.length === 0) return;
    syncRef.current = true;
    setIsSyncing(true);
    addToast("Connection restored  synchronizing data...", "info");

    const results = { success: 0, failed: 0 };
    const remaining = [];

    for (const item of queue) {
      try {
        if (item.type === "send_email") {
          await fetch("/api/email/send", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(item.payload) });
        } else if (item.type === "campaign_start") {
          await fetch(`/api/campaigns/${item.payload.id}/start`, { method: "POST" });
        } else if (item.type === "campaign_pause") {
          await fetch(`/api/campaigns/${item.payload.id}/pause`, { method: "POST" });
        } else if (item.type === "update_lead") {
          await fetch(`/api/leads/${item.payload.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(item.payload) });
        } else if (item.type === "create_draft") {
          await fetch("/api/email/draft", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(item.payload) });
        } else if (item.type === "update_contact") {
          await fetch(`/api/leads/${item.payload.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(item.payload) });
        }
        results.success++;
      } catch (e) {
        remaining.push({ ...item, status: "failed", error: e.message });
        results.failed++;
      }
    }

    setQueue(remaining);
    setIsSyncing(false);
    syncRef.current = false;

    if (results.failed === 0) {
      addToast(` Synchronization complete  ${results.success} items synced`, "success");
    } else {
      addToast(`Sync complete: ${results.success} synced, ${results.failed} failed`, "error");
    }
  }, [queue, addToast]);

  const toggleOffline = useCallback(() => {
    if (isOffline) {
      setIsOffline(false);
      addToast("Online mode activated  synchronizing...", "info");
      setTimeout(() => syncQueue(), 500);
    } else {
      setIsOffline(true);
      addToast("Working Offline  actions will be queued until reconnected", "info");
    }
  }, [isOffline, syncQueue, addToast]);

  const clearQueue = useCallback(() => {
    setQueue([]);
    localStorage.removeItem("sdr_offline_queue");
    addToast("Queue cleared", "info");
  }, [addToast]);

  const removeFromQueue = useCallback((id) => {
    setQueue(p => p.filter(i => i.id !== id));
  }, []);

  const effectiveOffline = isOffline || !browserOnline;

  return { isOffline, effectiveOffline, browserOnline, isSyncing, queue, toggleOffline, enqueue, syncQueue, clearQueue, removeFromQueue };
}

//  Status Bar Component 
export function OfflineStatusBar({ effectiveOffline, browserOnline, isOffline, isSyncing, queue, onToggle, onSync }) {
  const status = isSyncing ? "syncing" : effectiveOffline ? "offline" : "online";
  const cfg = {
    online:  { color: "#107c10", bg: "#dff6dd", label: "Connected",       dot: "#107c10", icon: "" },
    offline: { color: "#a4262c", bg: "#fde7e9", label: "Working Offline",  dot: "#a4262c", icon: "" },
    syncing: { color: "#d83b01", bg: "#fed9cc", label: "Synchronizing...", dot: "#f59e0b", icon: "" },
  }[status];

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 12px", background: cfg.bg, borderRadius: 4, border: `1px solid ${cfg.color}33`, userSelect: "none" }}>
      <span style={{ color: cfg.dot, fontSize: 12, animation: isSyncing ? "spin .7s linear infinite" : "none", display: "inline-block" }}>{cfg.icon}</span>
      <span style={{ fontSize: 11, color: cfg.color, fontWeight: 600, whiteSpace: "nowrap" }}>{cfg.label}</span>
      {!browserOnline && <span style={{ fontSize: 10, color: "#a4262c", fontStyle: "italic" }}>No internet</span>}
      {queue.length > 0 && (
        <span style={{ background: cfg.color, color: "#fff", fontSize: 10, padding: "1px 6px", borderRadius: 99, fontWeight: 700 }}>
          {queue.length} queued
        </span>
      )}
      {isSyncing && (
        <span style={{ fontSize: 10, color: cfg.color }}>syncing...</span>
      )}
      {!isSyncing && queue.length > 0 && !effectiveOffline && (
        <button onClick={onSync}
          style={{ background: cfg.color, color: "#fff", border: "none", borderRadius: 3, padding: "1px 7px", fontSize: 10, cursor: "pointer", fontFamily: "'Segoe UI',sans-serif" }}>
          Sync now
        </button>
      )}
    </div>
  );
}

//  Queue Panel Component 
export function QueuePanel({ queue, onClear, onRemove, onClose }) {
  const typeIcons = {
    send_email: "", campaign_start: "", campaign_pause: "",
    update_lead: "", create_draft: "", update_contact: "",
  };
  const statusColors = {
    pending: { bg: "#deecf9", tc: "#0078d4" },
    failed:  { bg: "#fde7e9", tc: "#a4262c" },
  };

  return (
    <div style={{ position: "fixed", bottom: 60, left: 220, width: 360, background: "#fff", border: "1px solid #d1cec9", borderRadius: 6, boxShadow: "0 8px 24px rgba(0,0,0,.15)", zIndex: 1000, fontFamily: "'Segoe UI',sans-serif", overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", background: "#f3f2f1", borderBottom: "1px solid #d1cec9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#201f1e" }}>Pending Queue ({queue.length})</span>
        <div style={{ display: "flex", gap: 6 }}>
          {queue.length > 0 && (
            <button onClick={onClear} style={{ background: "#fde7e9", color: "#a4262c", border: "none", borderRadius: 3, padding: "3px 8px", fontSize: 11, cursor: "pointer" }}>Clear all</button>
          )}
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#605e5c", fontSize: 16, cursor: "pointer", lineHeight: 1 }}></button>
        </div>
      </div>
      <div style={{ maxHeight: 300, overflowY: "auto" }}>
        {queue.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: "#605e5c", fontSize: 13 }}>No queued items</div>
        )}
        {queue.map(item => {
          const sc = statusColors[item.status] || statusColors.pending;
          return (
            <div key={item.id} style={{ padding: "8px 14px", borderBottom: "1px solid #f0efee", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{typeIcons[item.type] || ""}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 12, fontWeight: 500, color: "#201f1e", marginBottom: 2 }}>{item.label}</p>
                <p style={{ fontSize: 10, color: "#605e5c" }}>{new Date(item.queuedAt).toLocaleTimeString()}</p>
                {item.error && <p style={{ fontSize: 10, color: "#a4262c" }}>Error: {item.error}</p>}
              </div>
              <span style={{ background: sc.bg, color: sc.tc, fontSize: 10, padding: "2px 7px", borderRadius: 3, fontWeight: 500, flexShrink: 0 }}>
                {item.status === "failed" ? "Failed" : "Pending"}
              </span>
              <button onClick={() => onRemove(item.id)} style={{ background: "none", border: "none", color: "#a19f9d", fontSize: 14, cursor: "pointer", flexShrink: 0 }}></button>
            </div>
          );
        })}
      </div>
      <div style={{ padding: "8px 14px", background: "#f8f7f6", borderTop: "1px solid #d1cec9" }}>
        <p style={{ fontSize: 11, color: "#605e5c" }}>Items will sync automatically when online</p>
      </div>
    </div>
  );
}